import mapboxgl from "mapbox-gl";

import { featureCollection as turfFeatureCollection } from "@turf/helpers";
import turfEnvelope from "@turf/envelope";

import { flatMap, sortBy, sumBy } from "lodash";

import {
  getTreesPlanted,
  calculateForestArea,
  getPolygonCoordinatesFromArea,
  getRandomColour,
} from "../Functions/functions";
const MAIN_FOREST_SOURCE = "main_forest_source";
const MAIN_FOREST_LAYER = "main_forest_layer";

export const addForestDonations = (map, forestDonations, forestLng, forestLat) => {
  if (!map.getSource(MAIN_FOREST_SOURCE)) {
    const forestGeoJsonData = createForestSourceData(forestDonations, forestLng, forestLat);
    addMapSource(map, forestGeoJsonData);
    addMapLayer(map);
  } else {
    moveForestOnMap(map, forestDonations, forestLng, forestLat);
  }
};

export const createForestSourceData = (forestDonations, forestLng, forestLat, colours = []) => {
  let lastSegmentGeometry = null;
  let previousSegmentGeometry = [];
  let areaPlotted = null;
  const CUTOFF = 0.3;
  let previousSegmentIndex = null;
  let segmentCoordinates = null;

  const totalDonations = sumBy(forestDonations, (donation) => donation.donationAmount);
  const totalForestArea = calculateForestArea(getTreesPlanted(totalDonations));

  const sortedForestDonations = sortBy(
    forestDonations,
    (donation) => donation.donationAmount
  ).reverse();
  const forestGeoData = flatMap(sortedForestDonations, (donation, index) => {
    const treesPlanted = getTreesPlanted(donation.donationAmount);
    const segmentArea = calculateForestArea(treesPlanted);
    let newSegmentTopLeftCoordinate;
    if (!lastSegmentGeometry) {
      newSegmentTopLeftCoordinate = [forestLng, forestLat];
      segmentCoordinates = getPolygonCoordinatesFromArea(newSegmentTopLeftCoordinate, segmentArea);
    } else {
      const lastSegmentCoordinates = formatCoordinates(lastSegmentGeometry);
      if (areaPlotted < CUTOFF * totalForestArea) {
        newSegmentTopLeftCoordinate = lastSegmentCoordinates.topRight;
        segmentCoordinates = getPolygonCoordinatesFromArea(
          newSegmentTopLeftCoordinate,
          segmentArea
        );
      } else if (previousSegmentIndex === null) {
        const firstSegmentGeometry = previousSegmentGeometry[0];
        const firstSegmentDraw = formatCoordinates(firstSegmentGeometry);
        newSegmentTopLeftCoordinate = firstSegmentDraw.bottomLeft;
        segmentCoordinates = getPolygonCoordinatesFromArea(
          newSegmentTopLeftCoordinate,
          segmentArea
        );
        previousSegmentIndex = 0;
      } else {
        newSegmentTopLeftCoordinate = lastSegmentCoordinates.topRight;
        segmentCoordinates = getPolygonCoordinatesFromArea(
          newSegmentTopLeftCoordinate,
          segmentArea
        );
        const [newSegmentRightLng, limitingSegmentRightLng] = getRightEdgeLngs(
          segmentCoordinates,
          previousSegmentGeometry,
          previousSegmentIndex
        );

        if (newSegmentRightLng > limitingSegmentRightLng) {
          const response = recursiveGetTopLeftCord(
            previousSegmentGeometry,
            previousSegmentIndex,
            segmentArea
          );
          segmentCoordinates = response[0];
          previousSegmentIndex = response[1];
        }
      }
    }

    const segmentGeometry = turfEnvelope(turfFeatureCollection(segmentCoordinates)).geometry;

    lastSegmentGeometry = segmentGeometry;
    previousSegmentGeometry.push(segmentGeometry);
    areaPlotted += segmentArea;
    const name = getName(donation);
    const id = createId(donation.id);

    return {
      type: "Feature",
      properties: {
        name,
        colour: colours[index] ? colours[index] : getRandomColour(),
        amount: donation.donationAmount,
        id,
      },
      id,
      geometry: segmentGeometry,
    };
  });
  return turfFeatureCollection(forestGeoData);
};
const formatCoordinates = (polygon) => {
  const coordinates = polygon.coordinates[0];
  return {
    bottomLeft: coordinates[0],
    bottomRight: coordinates[1],
    topRight: coordinates[2],
    topLeft: coordinates[3],
  };
};
const getName = (donation) => {
  if (donation.displayName === true && donation.name) {
    return donation.name;
  } else {
    return "An Anonymous Supporter";
  }
};
const createId = (documentId) => {
  return Math.round(Math.random() * 100000);
};
const getRightEdgeLngs = (segmentCoordinates, previousSegmentGeometry, previousSegmentIndex) => {
  const newSegmentBottomRight = segmentCoordinates[1].geometry.coordinates;
  const newSegmentRightLng = newSegmentBottomRight[0];
  const limitingSegmentCoordinates = formatCoordinates(
    previousSegmentGeometry[previousSegmentIndex]
  );
  const limitingSegmentRightLng = limitingSegmentCoordinates.bottomRight[0];
  return [newSegmentRightLng, limitingSegmentRightLng];
};

const recursiveGetTopLeftCord = (previousSegmentGeometry, previousSegmentIndex, segmentArea) => {
  previousSegmentIndex += 1;

  const nextLargeSegmentCoordinates = formatCoordinates(
    previousSegmentGeometry[previousSegmentIndex]
  );
  const newSegmentTopLeftCoordinate = nextLargeSegmentCoordinates.bottomLeft;
  const segmentCoordinates = getPolygonCoordinatesFromArea(
    newSegmentTopLeftCoordinate,
    segmentArea
  );
  return [segmentCoordinates, previousSegmentIndex];
};
export const moveForestOnMap = (map, forestDonations, forestLng, forestLat) => {
  const currentForestArea = map.getSource(MAIN_FOREST_SOURCE);
  if (currentForestArea) {
    const colours = flatMap(
      currentForestArea._data.features,
      (segment) => segment.properties.colour
    );
    const forestGeoJsonData = createForestSourceData(
      forestDonations,
      forestLng,
      forestLat,
      colours
    );
    map.getSource(MAIN_FOREST_SOURCE).setData(forestGeoJsonData);
  }
};

export const updateForestLocationInState = (newLng, newLat, setForestLng, setForestLat) => {
  setForestLng(newLng);
  setForestLat(newLat);
};

export const addMapSource = (map, geoJsonData) => {
  if (!map.getSource(MAIN_FOREST_SOURCE)) {
    map.addSource(MAIN_FOREST_SOURCE, {
      type: "geojson",
      data: geoJsonData,
    });
  }
};

export const addFillOnHover = (map, mapEvent, donateeInfo, setDonateeInfo) => {
  if (mapEvent) {
    if (mapEvent.length > 0) {
      const currentHoveredSegment = mapEvent[0];
      if (currentHoveredSegment.properties.id !== donateeInfo.id) {
        setDonateeInfo({
          id: currentHoveredSegment.properties.id,
          name: currentHoveredSegment.properties.name,
          amount: currentHoveredSegment.properties.amount,
        });
        removeHoverStateEffects(map, donateeInfo.id);
        map.setFeatureState(
          { source: MAIN_FOREST_SOURCE, id: currentHoveredSegment.id },
          { hover: true }
        );
      }
    }
  }
};

export const removeHoverStateEffects = (map, id) => {
  map.removeFeatureState({
    source: MAIN_FOREST_SOURCE,
    id: id,
  });
};

export const removeFillOnLeave = (map, mapEvent, donateeInfo, setDonateeInfo) => {
  if (mapEvent && donateeInfo) {
    if (donateeInfo.id) {
      removeHoverStateEffects(map, donateeInfo.id);
      setDonateeInfo({
        id: null,
        name: null,
        amount: null,
      });
    }
  }
};

export const addMapLayer = (map) => {
  if (!map.getLayer(MAIN_FOREST_LAYER)) {
    map.addLayer({
      id: MAIN_FOREST_LAYER,
      type: "fill",
      source: MAIN_FOREST_SOURCE,
      paint: {
        "fill-color": ["get", "colour"],
        "fill-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 1, 0.4],
      },
    });
    map.addLayer({
      id: "state-borders",
      type: "line",
      source: MAIN_FOREST_SOURCE,
      layout: {},
      paint: {
        "line-color": "#000000",
        "line-width": 2,
      },
    });
  }
};

export const addMapControls = (map) => {
  map.addControl(new mapboxgl.NavigationControl(), "bottom-right");

  map.addControl(
    new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: true,
    })
  );
};

export const updateMapState = (map, setLat, setLng, setZoom) => {
  setLat(map.getCenter().lat.toFixed(5));
  setLng(map.getCenter().lng.toFixed(5));
  setZoom(map.getZoom().toFixed(2));
};
